import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useAuthStore } from './stores/auth.store';
import { useNotificationStore } from './stores/notification.store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import oneSignalService from './services/onesignal.service';
import { initAnalytics } from './helpers/analytics.helper';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Supply from './pages/Supply';
import AppetiteDashboard from './pages/AppetiteDashboard';
import Orders from './pages/Orders';
import Materials from './pages/Materials';
import Notifications from './pages/Notifications';
import Layout from './components/Layout';
import DemoEntry from './pages/demo/DemoEntry';
import { isDemoMode } from './demo/core';
import AuthRoute from './components/AuthRoute';
import {
  APPETITE_READ_ROLES,
  APPETITE_WRITE_ROLES,
  PERM_APPETITE_READ,
  PERM_APPETITE_WRITE,
  PERM_PRE_ORDER_LIST,
  PRE_ORDER_READ_ROLES,
  ORGANIZATION_READ_ROLES,
  PERM_ORGANIZATION_READ,
} from './auth';
import HelpI18nProvider from './components/help/HelpI18nProvider';
import AppI18nProvider from './components/AppI18nProvider';
import KnowledgeBase from './pages/support/KnowledgeBase';
import KnowledgeBaseArticle from './pages/support/KnowledgeBaseArticle';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { appI18n, getLanguageDir } from './i18n/app';
import { APP_FONT_FAMILY } from './constants/fonts';
import './i18n/app';

const queryClient = new QueryClient();

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const rtlCache = createCache({
  key: 'mui-rtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const baseTheme = {
  typography: {
    fontFamily: APP_FONT_FAMILY,
    fontSize: 14,
    htmlFontSize: 16,
    h1: {
      fontWeight: 700,
      letterSpacing: 0,
    },
    h2: {
      fontWeight: 700,
      letterSpacing: 0,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: 0,
    },
    h4: {
      fontWeight: 600,
      fontSize: '2.125rem',
      lineHeight: 1.235,
      letterSpacing: 0,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.334,
      letterSpacing: 0,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: 1.45,
      letterSpacing: 0,
    },
    subtitle2: {
      fontSize: '0.875rem',
      lineHeight: 1.45,
      letterSpacing: 0,
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: 0,
      textTransform: 'none' as const,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.45,
      letterSpacing: 0,
    },
    overline: {
      fontSize: '0.75rem',
      letterSpacing: 0,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: APP_FONT_FAMILY,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: APP_FONT_FAMILY,
          letterSpacing: 0,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: APP_FONT_FAMILY,
        },
        input: {
          fontFamily: APP_FONT_FAMILY,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: APP_FONT_FAMILY,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: APP_FONT_FAMILY,
        },
        secondary: {
          fontFamily: APP_FONT_FAMILY,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: APP_FONT_FAMILY,
        },
        head: {
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: 0,
        },
        body: {
          fontSize: '0.875rem',
          letterSpacing: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid',
        },
      },
    },
  },
};

function ProtectedLayout({ onThemeToggle }: { onThemeToggle: () => void }) {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Layout onThemeToggle={onThemeToggle} />;
}

function App() {
  const { isAuthenticated, initAuth } = useAuthStore();
  const oneSignalInitialized = useRef(false);
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (
      (savedMode as 'light' | 'dark') ||
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    );
  });

  const [direction, setDirection] = useState<'ltr' | 'rtl'>(() => getLanguageDir());

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setDirection(getLanguageDir(lng));
    appI18n.on('languageChanged', onLanguageChanged);
    return () => {
      appI18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        ...baseTheme,
        direction,
        palette: {
          mode,
          primary: {
            main: '#2cb259',
            light: '#4cd679',
            dark: '#1a8f47',
            contrastText: '#FFFFFF',
          },
          secondary: {
            main: '#0EA5E9',
            light: '#38BDF8',
            dark: '#0284C7',
            contrastText: '#FFFFFF',
          },
          background: {
            default: mode === 'dark' ? '#0F172A' : '#F1F5F9',
            paper: mode === 'dark' ? '#1E293B' : '#FFFFFF',
          },
          text: {
            primary: mode === 'dark' ? '#F8FAFC' : '#1E293B',
            secondary: mode === 'dark' ? '#94A3B8' : '#64748B',
          },
          divider:
            mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.12)',
        },
        components: {
          ...baseTheme.components,
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                fontFamily: APP_FONT_FAMILY,
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' ? '#1E293B' : '#FFFFFF',
                color: mode === 'dark' ? '#F8FAFC' : '#1E293B',
                borderColor:
                  mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'dark' ? '#1E293B' : '#FFFFFF',
                borderColor:
                  mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                backgroundColor: mode === 'dark' ? '#1E293B' : '#FFFFFF',
                border:
                  mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.06)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow:
                  mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'dark' ? '#1E293B' : '#FFFFFF',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                fontFamily: APP_FONT_FAMILY,
                padding: '8px 16px',
                borderRadius: 12,
                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
              },
              contained: {
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 20px rgba(44, 178, 89, 0.35)',
                },
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                fontFamily: APP_FONT_FAMILY,
              },
              head: {
                fontWeight: 600,
                fontSize: '0.875rem',
                letterSpacing: 0,
              },
              body: {
                fontSize: '0.875rem',
                letterSpacing: 0,
              },
            },
          },
        },
      }),
    [mode, direction]
  );

  useEffect(() => {
    initAnalytics();
    initAuth();

    if (!oneSignalInitialized.current) {
      oneSignalInitialized.current = true;
      oneSignalService.initialize();
    }
  }, [initAuth]);

  useEffect(() => {
    // Demo tabs never use OneSignal (isReady() stays false), so don't poll for it.
    if (isAuthenticated && !isDemoMode()) {
      const setupHandler = async () => {
        if (!oneSignalService.isReady()) {
          setTimeout(setupHandler, 1000);
          return;
        }

        const handleNotificationClick = async (notification: { id?: string; notificationId?: string }) => {
          const notificationId = notification.id || notification.notificationId;

          const savedOrg = sessionStorage.getItem('selectedOrganization');
          let organizationId: string | null = null;

          if (savedOrg) {
            try {
              const parsedOrg = JSON.parse(savedOrg) as { id: string };
              organizationId = parsedOrg.id;
            } catch (e) {
              console.error('Error parsing saved organization:', e);
            }
          }

          if (organizationId && notificationId) {
            try {
              await useNotificationStore.getState().markAsRead(organizationId, notificationId);
            } catch (error) {
              console.error('Error marking notification as read:', error);
            }
          }
        };

        oneSignalService.setCustomClickHandler(handleNotificationClick);
      };

      void setupHandler();
    }
  }, [isAuthenticated]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppI18nProvider>
        <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
            <Routes>
              {/* Public demo (no login). Market is recycler-only. */}
              <Route path="/demo/outlet" element={<Navigate to="/demo/recycler" replace />} />
              <Route path="/demo/:persona" element={<DemoEntry />} />
              <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/supply" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/knowledge-base"
                element={
                  isAuthenticated ? (
                    <Layout onThemeToggle={toggleTheme}>
                      <HelpI18nProvider>
                        <KnowledgeBase />
                      </HelpI18nProvider>
                    </Layout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/knowledge-base/:articleId"
                element={
                  isAuthenticated ? (
                    <Layout onThemeToggle={toggleTheme}>
                      <HelpI18nProvider>
                        <KnowledgeBaseArticle />
                      </HelpI18nProvider>
                    </Layout>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route element={<ProtectedLayout onThemeToggle={toggleTheme} />}>
                <Route
                  path="/materials"
                  element={
                    <AuthRoute
                      orgScopedPermission={PERM_ORGANIZATION_READ}
                      legacyRoles={ORGANIZATION_READ_ROLES}
                    >
                      <Materials />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/supply"
                  element={
                    <AuthRoute
                      orgScopedPermission={PERM_APPETITE_WRITE}
                      legacyRoles={APPETITE_WRITE_ROLES}
                    >
                      <Supply />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <AuthRoute
                      orgScopedPermission={PERM_APPETITE_READ}
                      legacyRoles={APPETITE_READ_ROLES}
                    >
                      <AppetiteDashboard />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <AuthRoute
                      orgScopedPermission={PERM_PRE_ORDER_LIST}
                      legacyRoles={PRE_ORDER_READ_ROLES}
                    >
                      <Orders />
                    </AuthRoute>
                  }
                />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/" element={<Navigate to="/supply" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/supply" replace />} />
            </Routes>
          </Router>
        </ThemeProvider>
      </CacheProvider>
      </AppI18nProvider>
    </QueryClientProvider>
  );
}

export default App;
